import React, { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useEventListener, usePreviousRef } from '@direct-frontend/stdlib';
import { getPlatformStyles } from '@direct-frontend/platform-code';

import { ListBox, ListBoxVirtualized } from '../ListBox';
import { List } from '../List';
import { Popup } from '../Popup';
import { cn } from '../../lib/css/className/className';

import { ComboboxProps } from './types';
import {
    autoFocusProps,
    defaultChildren,
    useComboboxProps,
    isComboboxStateVirtualized,
    isComboboxVirtualized,
    isFocusable,
} from './utils';

// В TagGroup на мобиле используется десктопный Combobox, для него нужны специальные стили
getPlatformStyles({ mobile: import('./Comboboxdesktop@mobile.css') });

const cls = cn('Combobox');

const emptyObj = {};

const Combobox = forwardRef(function Combobox<ValueType extends string, SectionType extends string = ValueType>(
    props: ComboboxProps<ValueType, SectionType>,
    forwardedRef: ForwardedRef<HTMLInputElement>,
) {
    const defaultWrapperRef = useRef<HTMLDivElement>(null);
    const {
        items,
        menuTrigger = 'focus',
        children = defaultChildren,
        size = 'm',
        className,
        addonBeforeSection: propsAddonBeforeSection,
        pinnedSectionAfter: propsPinnedSectionAfter,
        emptySection: propsEmptySection,
        containerRef,
        maxHeight,
        flip,
        displaySelection = true,
        width = 'max',
        'data-testid': dataTestId = 'Combobox',
        manualControl,
    } = props;

    const allowsEmptyCollection = Boolean(propsEmptySection || propsPinnedSectionAfter || props.pinnedSection);

    const { LogProvider, state, listBoxRef, inputRef, popoverRef, inputProps, listBoxProps } = useComboboxProps(
        {
            ...props,
            allowsEmptyCollection,
        },
        forwardedRef,
    );
    const wrapperRef = containerRef ?? defaultWrapperRef;
    const prevItems = usePreviousRef<typeof items | null>(items, null).current;

    const { isFocused, isOpen, open } = state;
    const onInputClick = useCallback(() => {
        // При нажатии на esc саджест закрывается, но инпут всё еще в фокусе.
        // В таком случае при клике в инпут открываем саджест
        if (!isOpen && isFocused && menuTrigger === 'focus') {
            open();
        }
    }, [isFocused, isOpen, menuTrigger, open]);

    const addonBeforeSection = useMemo(
        () =>
            typeof propsAddonBeforeSection === 'function'
                ? propsAddonBeforeSection(state.close)
                : propsAddonBeforeSection,
        [propsAddonBeforeSection, state.close],
    );

    const pinnedSectionAfter = useMemo(
        () =>
            typeof propsPinnedSectionAfter === 'function'
                ? propsPinnedSectionAfter(state.close)
                : propsPinnedSectionAfter,
        [propsPinnedSectionAfter, state.close],
    );

    const emptySection = useMemo(
        () => (typeof propsEmptySection === 'function' ? propsEmptySection(state.close) : propsEmptySection),
        [propsEmptySection, state.close],
    );

    useEffect(() => {
        // Открываем саджест, если пришли новые данные
        if (!state.isOpen && state.isFocused && menuTrigger === 'focus' && items !== prevItems) {
            state.open();
        }
    }, [items, menuTrigger, prevItems, state]);

    const handlePopupMouseDown = useCallback(
        (e: MouseEvent) => {
            if (popoverRef.current && !isFocusable(e.target)) {
                e.preventDefault();
            }
        },
        [popoverRef],
    );

    const isPopupShown = state.isOpen && (items.length > 0 || allowsEmptyCollection);

    // нужно для предотвращения закрытия попапа из-за потери фокуса инпута при клике мимо опций
    // (например, клик в разделители секций, в аддоны или отступы списка)
    // если использовать onPointerDown падают тесты из-за бага в user-event https://github.com/testing-library/user-event/issues/1206
    // но для desktop версии должно быть достаточно обработки mousedown события
    // хак isPopupShown ? emptyObj : undefined нужен для перепривязки хендлера к новой рефке после открытия попапа
    useEventListener('mousedown', handlePopupMouseDown, popoverRef, isPopupShown ? emptyObj : undefined);

    useImperativeHandle(manualControl, () => {
        return {
            open: state.open,
            close: state.close,
            get isOpen() {
                return state.isOpen;
            },
        };
    }, [state.open, state.close, state.isOpen]);

    let popupContent = null;

    if (state.collection.size === 0 && emptySection) {
        popupContent = (
            <List size={size} key="empty" width={width}>
                {emptySection}
            </List>
        );
    } else if (isComboboxStateVirtualized(props, state) && isComboboxVirtualized(props)) {
        popupContent = (
            <ListBoxVirtualized
                {...listBoxProps}
                className={cls('listbox')}
                addonBeforeSection={addonBeforeSection}
                selectionMode={'single'}
                ref={listBoxRef}
                state={state}
                items={props.items}
                size={size}
                width={width}
                data-testid={`${props['data-testid'] ?? 'Combobox'}.listBox`}
                displaySelection={displaySelection}
                pinnedSection={props.pinnedSection}
                pinnedSectionAfter={pinnedSectionAfter}
            />
        );
    } else {
        popupContent = (
            <ListBox
                {...listBoxProps}
                className={cls('listbox')}
                addonBeforeSection={addonBeforeSection}
                selectionMode={'single'}
                ref={listBoxRef}
                state={state}
                items={props.items}
                size={size}
                width={width}
                data-testid={`${props['data-testid'] ?? 'Combobox'}.listBox`}
                displaySelection={displaySelection}
                pinnedSection={props.pinnedSection}
                pinnedSectionAfter={pinnedSectionAfter}
            />
        );
    }

    return (
        <LogProvider>
            <div
                ref={defaultWrapperRef}
                className={cls(null, [className])}
                onClick={onInputClick}
                data-testid={dataTestId}
            >
                {children({
                    ...props,
                    ...inputProps,
                    onChange: state.setInputValue,
                    size,
                    // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                    value: inputProps.value as string,
                    ref: inputRef,
                    'data-testid': `${props['data-testid'] ?? 'Combobox'}.Textinput`,
                    autoComplete:
                        inputProps.autoComplete === 'off' || inputProps.autoComplete === 'on'
                            ? inputProps.autoComplete
                            : undefined,
                    spellCheck: String(inputProps.spellCheck) === 'true',
                })}
            </div>

            {isPopupShown && (
                <Popup
                    triggerRef={wrapperRef}
                    ref={popoverRef}
                    isOpen={state.isOpen}
                    onClose={state.close}
                    isModal={false}
                    isDismissable={false}
                    autoFocusProps={autoFocusProps}
                    placement={'bottom-start'}
                    offset={4}
                    flip={flip}
                    maxHeight={maxHeight}
                    matchTriggerWidth={width === 'max'}
                    data-testid={`${props['data-testid'] ?? 'Combobox'}.popup`}
                >
                    {popupContent}
                </Popup>
            )}
        </LogProvider>
    );
});

export default Combobox;
